import { useEffect, useRef } from 'react'
import { gsap } from 'gsap'
import './Hero.css'

export default function Hero({ onBookCall }) {
  return (
    <section className="hero" id="hero">
      <div className="hero__bg-photo" aria-hidden="true" />
      <div className="hero__bg-overlay" aria-hidden="true" />
      <div className="hero__bg-grid" aria-hidden="true" />
      <div className="hero__bg-glow" aria-hidden="true" />

      <div className="container hero__inner">
        <div className="hero__content">
          <span className="hero__location">
            <PinIcon /> Huddersfield &middot; Serving Leeds &amp; Manchester
          </span>

          <h1 className="hero__headline">
            AI Agents That<br />
            <span className="hero__headline-accent">Actually Work</span>
          </h1>

          <p className="hero__subheadline">
            We help ambitious Northern businesses move from AI curiosity
            to AI in production — with hands-on implementation, not slide decks.
          </p>

          <div className="hero__actions">
            <button className="btn btn--primary btn--primary-lg hero__cta" onClick={onBookCall}>
              Book a Free Discovery Call
              <ArrowIcon />
            </button>
            <a className="hero__learn" href="#services"
               onClick={(e) => { e.preventDefault(); document.querySelector('#services')?.scrollIntoView({ behavior: 'smooth' }) }}>
              See what we do
            </a>
          </div>
        </div>

        <div className="hero__visual" aria-hidden="true">
          <HeroVisual />
        </div>
      </div>

      <div className="hero__scroll-hint" aria-hidden="true">
        <span />
      </div>
    </section>
  )
}

// Satellite node positions + labels
const NODES = [
  { cx: 240, cy: 92,  label: 'CRM',     lx: 240, ly: 58  },
  { cx: 240, cy: 328, label: 'ERP',     lx: 240, ly: 364 },
  { cx: 122, cy: 210, label: 'Data',    lx: 84,  ly: 214 },
  { cx: 358, cy: 210, label: 'Email',   lx: 396, ly: 214 },
  { cx: 143, cy: 118, label: 'Finance', lx: 113, ly: 102 },
  { cx: 337, cy: 118, label: 'Docs',    lx: 367, ly: 102 },
  { cx: 143, cy: 302, label: 'APIs',    lx: 113, ly: 316 },
  { cx: 337, cy: 302, label: 'Comms',   lx: 367, ly: 316 },
]

// Spokes: from central ring edge to satellite (start coords match central r=48 boundary)
const SPOKES = [
  { x1: 240, y1: 162, x2: 240, y2: 100 },
  { x1: 240, y1: 258, x2: 240, y2: 320 },
  { x1: 192, y1: 210, x2: 130, y2: 210 },
  { x1: 288, y1: 210, x2: 350, y2: 210 },
  { x1: 206, y1: 176, x2: 155, y2: 130 },
  { x1: 274, y1: 176, x2: 325, y2: 130 },
  { x1: 206, y1: 244, x2: 155, y2: 290 },
  { x1: 274, y1: 244, x2: 325, y2: 290 },
]

function HeroVisual() {
  const svgRef        = useRef(null)
  const orbitRef      = useRef(null)
  const coreRing1Ref  = useRef(null)
  const coreRing2Ref  = useRef(null)
  const coreDotRef    = useRef(null)
  const spokeRefs     = useRef([])
  const nodeRefs      = useRef([])
  const labelRefs     = useRef([])
  const pulseRefs     = useRef([])   // travelling pulse dots on spokes

  useEffect(() => {
    const ctx = gsap.context(() => {
      const tl = gsap.timeline({ defaults: { ease: 'power2.out' } })

      // 1. Fade in the whole SVG
      tl.fromTo(svgRef.current, { opacity: 0 }, { opacity: 1, duration: 0.6 })

      // 2. Core breathe — outer rings pulse in
      tl.fromTo([coreRing1Ref.current, coreRing2Ref.current],
        { scale: 0.4, opacity: 0, transformOrigin: '240px 210px' },
        { scale: 1, opacity: 1, duration: 0.8, stagger: 0.15 }, '<0.2')

      tl.fromTo(coreDotRef.current,
        { scale: 0, opacity: 0, transformOrigin: '240px 210px' },
        { scale: 1, opacity: 1, duration: 0.5 }, '<0.1')

      // 3. Spokes draw in (strokeDashoffset trick)
      spokeRefs.current.forEach((el, i) => {
        if (!el) return
        const len = el.getTotalLength?.() ?? 80
        gsap.set(el, { strokeDasharray: len, strokeDashoffset: len })
        tl.to(el, { strokeDashoffset: 0, duration: 0.4, ease: 'power1.inOut' }, `<${i === 0 ? 0.15 : 0.06}`)
      })

      // 4. Satellite nodes pop in with stagger
      tl.fromTo(nodeRefs.current,
        { scale: 0, opacity: 0, transformOrigin: (i) => `${NODES[i].cx}px ${NODES[i].cy}px` },
        { scale: 1, opacity: 1, duration: 0.45, stagger: 0.07, ease: 'back.out(1.8)' }, '<0.1')

      // 5. Labels fade in after nodes
      tl.fromTo(labelRefs.current,
        { opacity: 0, y: 4 },
        { opacity: 1, y: 0, duration: 0.4, stagger: 0.05 }, '<0.2')

      // ─── Idle loops ───────────────────────────────────────────

      // Orbit ring slow rotation
      gsap.to(orbitRef.current, {
        rotation: 360,
        transformOrigin: '240px 210px',
        duration: 24,
        ease: 'none',
        repeat: -1,
      })

      // Core outer ring breathe
      gsap.to(coreRing1Ref.current, {
        scale: 1.12,
        opacity: 0.5,
        transformOrigin: '240px 210px',
        duration: 2.8,
        ease: 'sine.inOut',
        yoyo: true,
        repeat: -1,
      })

      // Core dot subtle pulse
      gsap.to(coreDotRef.current, {
        scale: 1.18,
        transformOrigin: '240px 210px',
        duration: 1.8,
        ease: 'sine.inOut',
        yoyo: true,
        repeat: -1,
      })

      // Satellite nodes — gentle float with staggered phase
      nodeRefs.current.forEach((el, i) => {
        if (!el) return
        gsap.to(el, {
          y: i % 2 === 0 ? -4 : 4,
          duration: 2.2 + i * 0.18,
          ease: 'sine.inOut',
          yoyo: true,
          repeat: -1,
          delay: i * 0.3,
        })
      })

      // Travelling pulse along each spoke
      pulseRefs.current.forEach((el, i) => {
        if (!el) return
        const s = SPOKES[i]
        const dx = s.x2 - s.x1
        const dy = s.y2 - s.y1
        gsap.set(el, { opacity: 0, x: 0, y: 0 })
        gsap.to(el, {
          motionPath: { path: [{ x: 0, y: 0 }, { x: dx, y: dy }], type: 'linear' },
          opacity: 0.9,
          duration: 1.4,
          ease: 'power1.inOut',
          repeat: -1,
          repeatDelay: 1.2 + i * 0.4,
          delay: i * 0.55,
          onRepeat: () => gsap.set(el, { x: 0, y: 0 }),
          keyframes: [
            { opacity: 0, x: 0, y: 0,         duration: 0.05 },
            { opacity: 0.9,                    duration: 0.6  },
            { opacity: 0, x: dx, y: dy,        duration: 0.75 },
          ],
        })
      })

    }, svgRef)

    return () => ctx.revert()
  }, [])

  return (
    <svg ref={svgRef} className="hero__svg" viewBox="0 0 480 420" fill="none"
         xmlns="http://www.w3.org/2000/svg" style={{ opacity: 0 }}>

      {/* Dark backdrop behind the graph so it reads on the photo */}
      <circle cx="240" cy="210" r="205" fill="rgba(6,16,30,0.72)"/>
      <circle cx="240" cy="210" r="205" fill="none" stroke="rgba(46,134,193,0.18)" strokeWidth="1"/>

      {/* Orbit ring */}
      <circle ref={orbitRef} cx="240" cy="210" r="88"
              stroke="rgba(46,134,193,0.75)" strokeWidth="1.5" fill="none" strokeDasharray="3 6"/>

      {/* Central node rings */}
      <circle ref={coreRing1Ref} cx="240" cy="210" r="48"
              fill="rgba(46,134,193,0.35)" stroke="#2E86C1" strokeWidth="2.5"/>
      <circle ref={coreRing2Ref} cx="240" cy="210" r="32"
              fill="rgba(46,134,193,0.45)" stroke="#2E86C1" strokeWidth="2"/>
      <circle ref={coreDotRef}   cx="240" cy="210" r="18" fill="#2E86C1"/>

      {/* Centre label */}
      <text x="240" y="206" textAnchor="middle" fill="white" fontSize="9" fontWeight="700" fontFamily="Inter, sans-serif">AI</text>
      <text x="240" y="218" textAnchor="middle" fill="white" fontSize="9" fontWeight="700" fontFamily="Inter, sans-serif">AGENT</text>

      {/* Spokes */}
      {SPOKES.map((s, i) => (
        <line key={i}
              ref={el => spokeRefs.current[i] = el}
              x1={s.x1} y1={s.y1} x2={s.x2} y2={s.y2}
              stroke="#2E86C1" strokeWidth="2" strokeDasharray="4 3" opacity="0.95"/>
      ))}

      {/* Travelling pulse dots */}
      {SPOKES.map((s, i) => (
        <circle key={`pulse-${i}`}
                ref={el => pulseRefs.current[i] = el}
                cx={s.x1} cy={s.y1} r="3"
                fill="#5ba3d1" opacity="0"/>
      ))}

      {/* Satellite nodes */}
      {NODES.map((n, i) => (
        <g key={i} ref={el => nodeRefs.current[i] = el}>
          <circle cx={n.cx} cy={n.cy} r="18"
                  fill="rgba(6,18,38,0.88)" stroke="#2E86C1" strokeWidth="2"/>
          <circle cx={n.cx} cy={n.cy} r="8" fill="rgba(46,134,193,0.85)"/>
          <circle cx={n.cx} cy={n.cy} r="3.5" fill="#5daedf"/>
        </g>
      ))}

      {/* Labels */}
      {NODES.map((n, i) => (
        <text key={`lbl-${i}`}
              ref={el => labelRefs.current[i] = el}
              x={n.lx} y={n.ly}
              textAnchor="middle"
              fill="rgba(255,255,255,0.95)"
              fontSize={i < 4 ? 10 : 9}
              fontWeight="500"
              fontFamily="Inter, sans-serif">
          {n.label}
        </text>
      ))}
    </svg>
  )
}

function PinIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <path d="M8 1C5.24 1 3 3.24 3 6c0 4 5 9 5 9s5-5 5-9c0-2.76-2.24-5-5-5zm0 6.5A1.5 1.5 0 1 1 8 4a1.5 1.5 0 0 1 0 3.5z" fill="currentColor"/>
    </svg>
  )
}

function ArrowIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <path d="M3 8h10M9 4l4 4-4 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  )
}
